import { fail, pass } from "@/core/check";
import type { Lesson } from "@/core/types";
import { hatStrukturiertenVerweis } from "@/office/excel/formeln";
import { allTables, formulaCells, readWorkbook } from "@/office/excel/workbook";
import { nichtPruefbar } from "../schnittstelle";

export const lektionStrukturierteVerweise: Lesson = {
  id: "strukturierte-verweise",
  title: "Strukturierte Verweise",
  summary: "=SUMME(Umsatz[Betrag]) statt =SUMME(D2:D340) – Formeln, die sagen, was sie tun.",
  durationMinutes: 11,
  learningGoals: [
    "Strukturierte Verweise lesen und schreiben",
    "Den Unterschied zwischen Spaltenverweis und Zeilenverweis kennen",
    "Erklären, warum solche Formeln beim Wachsen der Tabelle richtig bleiben",
  ],
  steps: [
    {
      id: "lesen",
      title: "Wie ein strukturierter Verweis gelesen wird",
      explanation: [
        "Sobald ein Bereich eine Tabelle ist, kann man ihn beim Namen nennen. " +
          "Umsatz[Betrag] meint die Spalte „Betrag“ der Tabelle „Umsatz“ – ohne " +
          "Kopfzeile, ohne Ergebniszeile, genau die Daten. Der Name der Tabelle steht " +
          "vorn, der Spaltenname in eckigen Klammern.",
        "Es gibt ein paar feste Bezeichner für besondere Teile: Umsatz[#Alle] ist die " +
          "ganze Tabelle samt Kopf- und Ergebniszeile, Umsatz[#Kopfzeilen] nur die " +
          "Überschriften, Umsatz[#Daten] nur die Datenzeilen. Sie beginnen alle mit " +
          "einem Rautezeichen und sind ebenfalls sprachabhängig – im englischen Excel " +
          "heißen sie [#All], [#Headers], [#Data].",
        "Das @-Zeichen bedeutet „in dieser Zeile“: [@Menge] innerhalb der Tabelle " +
          "verweist auf die Zelle der Spalte „Menge“ in derselben Zeile. Damit lassen " +
          "sich Rechenspalten schreiben, die ohne Zeilennummern auskommen – " +
          "=[@Menge]*[@Preis] gilt für jede Zeile gleichermaßen.",
        "Geschrieben werden diese Verweise wie alle anderen: durch Anklicken. Zeigst du " +
          "mit der Maus auf die Spaltenüberschrift einer Tabelle, wird der Zeiger zum " +
          "Pfeil nach unten; ein Klick markiert die Datenzellen der Spalte und setzt " +
          "den strukturierten Verweis in die offene Formel.",
      ],
      keyPoints: [
        "Tabelle[Spalte] meint die Datenzellen dieser Spalte, ohne Kopfzeile.",
        "[@Spalte] meint die Zelle derselben Zeile – für Rechenspalten in der Tabelle.",
        "[#Alle], [#Kopfzeilen], [#Daten] sprechen die Teile der Tabelle an.",
      ],
    },
    {
      id: "rechenspalte",
      title: "Eine Rechenspalte in der Tabelle",
      explanation: [
        "In einer Tabelle wird eine Formel nur einmal geschrieben. Tippst du in eine " +
          "leere Spalte neben der Tabelle eine Formel und bestätigst, füllt Excel sie " +
          "sofort über alle Zeilen aus und macht die Spalte zum Teil der Tabelle. Diese " +
          "Automatik heißt „berechnete Spalte“ und ist einer der stärksten Gründe für " +
          "Tabellen.",
        "Der eigentliche Gewinn zeigt sich beim Wachsen: Kommt unten eine neue Zeile " +
          "dazu, ist die Formel dort schon da. Kein Ausfüllen, kein Vergessen, keine " +
          "Lücke in der Rechenspalte – genau der Fehler, den du im Modul über Formeln " +
          "noch von Hand suchen musstest.",
        "Wenn du eine einzelne Zelle der berechneten Spalte überschreibst, meldet sich " +
          "Excel mit einem kleinen Blitzsymbol und bietet an, die Spalte " +
          "wiederherzustellen. Diese Warnung ist ernst zu nehmen – eine abweichende " +
          "Zelle in einer Rechenspalte ist fast immer ein Versehen.",
      ],
      keyPoints: [
        "Eine Formel neben der Tabelle wird automatisch zur berechneten Spalte.",
        "Neue Zeilen erben die Formel, ohne dass etwas ausgefüllt werden muss.",
        "Das Blitzsymbol meldet, wenn eine Zelle von der Spaltenformel abweicht.",
      ],
      task: {
        instruction:
          "Ergänze deine Tabelle um eine Rechenspalte, deren Formel strukturierte " +
          "Verweise benutzt – zum Beispiel =[@Menge]*[@Preis].",
        hint: "Schreibe die Überschrift in die erste freie Spalte neben der Tabelle, dann in die Zelle darunter die Formel. Klicke die Spalten an, statt sie zu tippen.",
        check: async () => {
          const mappe = await readWorkbook();
          if (!mappe.features.cells) {
            return nichtPruefbar(
              "Die Formeln der Zellen",
              "1.4",
              "Sieh in der Bearbeitungsleiste nach, ob dort ein Spaltenname in eckigen Klammern steht.",
              true,
            );
          }

          const tabellen = allTables(mappe);
          if (tabellen.length === 0) {
            return fail(
              "In der Arbeitsmappe gibt es noch keine Tabelle.",
              "Wandle deine Liste zuerst mit Strg + T in eine Tabelle um.",
            );
          }

          const formeln = formulaCells(mappe);
          const strukturiert = formeln.filter((zelle) => hatStrukturiertenVerweis(zelle.formula));

          if (strukturiert.length === 0) {
            return fail(
              `Von ${formeln.length} Formeln benutzt keine einen strukturierten Verweis.`,
              "Setze in der ersten freien Spalte neben der Tabelle eine Formel und klicke die " +
                "Spalten an, statt Zelladressen zu tippen. Excel schreibt dann [@Menge] statt B2.",
            );
          }

          const erste = strukturiert[0];
          return pass(
            `In ${erste.address} steht „${erste.formula}“ – ein Verweis, der sagt, was er meint, ` +
              "und der auch dann noch stimmt, wenn die Tabelle wächst." +
              (strukturiert.length > 1 ? ` Insgesamt ${strukturiert.length} solcher Formeln.` : ""),
          );
        },
      },
    },
    {
      id: "auswertung-ausserhalb",
      title: "Auswertungen außerhalb der Tabelle",
      explanation: [
        "Auch von außerhalb lässt sich auf eine Tabelle zeigen – dann mit dem " +
          "Tabellennamen davor: =SUMME(Umsatz[Betrag]). Steht diese Summe unter der " +
          "Tabelle oder auf einem anderen Blatt, wächst sie mit jeder neuen Zeile mit, " +
          "ohne dass jemand den Bereich anpassen muss.",
        "Genau dafür lohnt sich der Aufwand. Eine Auswertung, die auf einen festen " +
          "Bereich zeigt, hat ein Verfallsdatum: Sobald jemand unten Zeilen anfügt, " +
          "rechnet sie zu wenig – und tut das lautlos. Eine Auswertung auf eine Tabelle " +
          "kann dieses Problem gar nicht bekommen.",
        "Der Vergleich lohnt sich auch beim Lesen einer fremden Datei. " +
          "=SUMME(D2:D340) verlangt, dass man nachsieht, was in Spalte D steht und ob " +
          "Zeile 340 wirklich die letzte ist. =SUMME(Umsatz[Betrag]) beantwortet beides " +
          "im Vorbeigehen.",
      ],
      keyPoints: [
        "Von außen: =SUMME(Tabellenname[Spalte]).",
        "Solche Auswertungen wachsen automatisch mit der Tabelle.",
        "Ein fester Bereich in einer Auswertung ist ein Fehler mit Verzögerung.",
      ],
      task: {
        instruction:
          "Schreibe außerhalb deiner Tabelle – darunter oder auf einem anderen Blatt – " +
          "eine Auswertung, die über den Tabellennamen auf eine Spalte zugreift.",
        hint: "=SUMME(Umsatz[Betrag]). Tippe den Tabellennamen an und klicke dann die Spaltenüberschrift an.",
        check: async () => {
          const mappe = await readWorkbook();
          if (!mappe.features.cells) {
            return nichtPruefbar(
              "Die Formeln der Zellen",
              "1.4",
              "Sieh nach, ob deine Auswertung den Tabellennamen und einen Spaltennamen in eckigen Klammern enthält.",
              true,
            );
          }

          const tabellen = allTables(mappe);
          if (tabellen.length === 0) {
            return fail(
              "In der Arbeitsmappe gibt es noch keine Tabelle.",
              "Wandle deine Liste zuerst mit Strg + T in eine Tabelle um.",
            );
          }

          const namen = tabellen.map((tabelle) => tabelle.name).filter((name) => name.length > 0);
          const mitTabellenname = formulaCells(mappe).filter((zelle) =>
            namen.some((name) =>
              new RegExp(`(^|[^A-Za-zÄÖÜäöüß0-9_])${name.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}\\s*\\[`, "u").test(
                zelle.formula,
              ),
            ),
          );

          if (mitTabellenname.length === 0) {
            return fail(
              `Keine Formel verweist über den Tabellennamen auf ${namen.map((name) => `„${name}“`).join(" oder ")}.`,
              `Schreibe unter die Tabelle zum Beispiel =SUMME(${namen[0]}[` +
                `${tabellen[0].columns.find((spalte) => spalte.name.length > 0)?.name ?? "Spalte"}]).`,
            );
          }

          const erste = mitTabellenname[0];
          return pass(
            `In ${erste.address} steht „${erste.formula}“ und liefert „${erste.text}“. ` +
              "Diese Auswertung wächst mit jeder neuen Tabellenzeile mit.",
          );
        },
      },
    },
  ],
};
